/**
 * @fileoverview Shared backtest configuration store
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { ParameterRanges } from '../types/backtest';
import { StrategyRegistry } from '../config/strategyRegistry';
import { getQuoteCurrencyFromSymbol } from '../utils/enhancedCurrency';
import type { ShortSellingMode } from '../components/ui/AdvancedSettingsModal';
import { DEFAULT_TRADING_FEE_PERCENT } from '../constants';
import type { OptimizationTargetId } from '../constants/optimizationTargets';
import { DEFAULT_OPTIMIZATION_TARGET } from '../constants/optimizationTargets';

interface BacktestConfigurationStore {
  // Configuration state
  strategy: string;
  symbol: string;
  interval: string;
  portfolioAmount: number;
  portfolioCurrency: string;
  portfolioType: 'USD' | 'ASSET' | 'MIXED';
  parameterRanges: ParameterRanges;
  optimizationTarget: OptimizationTargetId;
  startDate: string;
  endDate: string;
  shortSellingMode: ShortSellingMode;
  capPercent: number;
  tradingFeePercent: number; // Trading fee percentage (0.1 = 0.1%)
  
  // Exit management state
  stopLossPercent: number;
  takeProfitPercent: number;
  trailingStopPercent: number;
  slippagePercent: number;
  
  // Candle range state
  rangeMode: 'date' | 'candle';
  startCandle: number;
  endCandle: number;
  totalCandles: number;
  candleLimit: number; // Server-side candle limit (e.g., 20000)
  
  // Baseline scan mode (multi-asset scan vs single asset)
  isBaselineScanMode: boolean;
  baselinePackId: string; // Selected baseline pack ID

  // Actions
  setStrategy: (strategy: string) => void;
  setSymbol: (symbol: string) => void;
  setInterval: (interval: string) => void;
  setPortfolioAmount: (amount: number) => void;
  setPortfolioCurrency: (currency: string) => void;
  setPortfolioType: (type: 'USD' | 'ASSET' | 'MIXED') => void;
  setParameterRanges: (ranges: ParameterRanges) => void;
  setOptimizationTarget: (target: OptimizationTargetId) => void;
  setStartDate: (date: string) => void;
  setEndDate: (date: string) => void;
  setShortSellingMode: (mode: ShortSellingMode) => void;
  setCapPercent: (percent: number) => void;
  setTradingFeePercent: (percent: number) => void;
  // Exit management actions
  setStopLossPercent: (percent: number) => void;
  setTakeProfitPercent: (percent: number) => void;
  setTrailingStopPercent: (percent: number) => void;
  setSlippagePercent: (percent: number) => void;
  setRangeMode: (mode: 'date' | 'candle') => void;
  setStartCandle: (candle: number) => void;
  setEndCandle: (candle: number) => void;
  setTotalCandles: (count: number) => void;
  setCandleLimit: (limit: number) => void;
  setIsBaselineScanMode: (mode: boolean) => void;
  setBaselinePackId: (packId: string) => void;
}

export const useBacktestConfigurationStore = create<BacktestConfigurationStore>()(
  devtools(
    (set) => ({
      // Initial state
      strategy: '',
      symbol: 'BTCUSDT',
      interval: '1d',
      portfolioAmount: 10000,
      portfolioCurrency: 'USDT',
      portfolioType: 'USD',
      parameterRanges: {},
      optimizationTarget: DEFAULT_OPTIMIZATION_TARGET,
      startDate: '',
      endDate: '',
      shortSellingMode: 'REINVEST_PROFITS',
      capPercent: 100,
      tradingFeePercent: DEFAULT_TRADING_FEE_PERCENT, // Use constant to stay in sync with server
      
      // Exit management initial state (0 = disabled)
      stopLossPercent: 0,
      takeProfitPercent: 0,
      trailingStopPercent: 0,
      slippagePercent: 0,
      
      // Candle range initial state - default to candle mode for better UX
      rangeMode: 'candle',
      startCandle: 1,
      endCandle: 2000,
      totalCandles: 0,
      candleLimit: 50000, // Default, will be updated from server
      
      // Baseline scan mode - default to baseline mode for new projects
      isBaselineScanMode: true,
      baselinePackId: 'crypto-major', // Default to crypto majors pack

      // Actions
      setStrategy: (strategy: string) => {
        set({ strategy });
        // Update parameter ranges when strategy changes
        const parameterRanges = StrategyRegistry.getParameterRanges(strategy);
        set({ parameterRanges });
      },

      setSymbol: (symbol: string) => {
        set({ symbol });
        // Auto-detect portfolio currency when symbol changes
        const detectedCurrency = getQuoteCurrencyFromSymbol(symbol);
        set({ portfolioCurrency: detectedCurrency });
      },

      setInterval: (interval: string) => set({ interval }),
      setPortfolioAmount: (portfolioAmount: number) => set({ portfolioAmount }),
      setPortfolioCurrency: (portfolioCurrency: string) => set({ portfolioCurrency }),
      setPortfolioType: (portfolioType: 'USD' | 'ASSET' | 'MIXED') => set({ portfolioType }),
      setParameterRanges: (parameterRanges: ParameterRanges) => set({ parameterRanges }),
      setOptimizationTarget: (optimizationTarget) => set({ optimizationTarget }),
      setStartDate: (startDate: string) => set({ startDate }),
      setEndDate: (endDate: string) => set({ endDate }),
      setShortSellingMode: (shortSellingMode: ShortSellingMode) => set({ shortSellingMode }),
      setCapPercent: (capPercent: number) => set({ capPercent }),
      setTradingFeePercent: (tradingFeePercent: number) => set({ tradingFeePercent }),
      // Exit management actions
      setStopLossPercent: (stopLossPercent: number) => set({ stopLossPercent }),
      setTakeProfitPercent: (takeProfitPercent: number) => set({ takeProfitPercent }),
      setTrailingStopPercent: (trailingStopPercent: number) => set({ trailingStopPercent }),
      setSlippagePercent: (slippagePercent: number) => set({ slippagePercent }),
      setRangeMode: (rangeMode: 'date' | 'candle') => set({ rangeMode }),
      setStartCandle: (startCandle: number) => set({ startCandle }),
      setEndCandle: (endCandle: number) => set({ endCandle }),
      setTotalCandles: (totalCandles: number) => set({ totalCandles }),
      setCandleLimit: (candleLimit: number) => set({ candleLimit }),
      setIsBaselineScanMode: (isBaselineScanMode: boolean) => set({ isBaselineScanMode }),
      setBaselinePackId: (baselinePackId: string) => set({ baselinePackId }),
    }),
    {
      name: 'backtest-configuration-store',
      enabled: import.meta.env.DEV,
    }
  )
);

// Helper to get a snapshot of the backtest execution configuration
// This lets callers pull the full configuration from one place instead of
// reading individual fields in multiple files. Use this when building
// optimization/preview/save requests so future fields only need to be added
// here.
export const getBacktestExecutionConfig = () => useBacktestConfigurationStore.getState();
